import { Button, Col, Heading, Row } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useReducer, useState } from 'react';
import { useTheme } from 'styled-components';
import type { OrganizationDataError } from '@common/interfaces/api/organizationDataErrorsApi';
import type { IOrganizationValidated } from '@common/validation/organization';
import type { IOrganization } from '@frontend/components/interfaces/organization';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization, OrganizationKgoStatus } from '@common/enums';
import { OrganizationStructureType } from '@common/enums/organizationStructureType';
import { formatString } from '@common/utils/formatString';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { emptyUuid } from '@common/utils/uuid';
import { organizationSchema } from '@common/validation/organization';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { KgbSuccessPage } from '@frontend/components/elements/KgbSuccessPage';
import { ContactDetailsCard } from '@frontend/components/pages/organizations/organizationAdd/ContactDetailsCard';
import { GeneralInformationCard } from '@frontend/components/pages/organizations/organizationAdd/GeneralInformationCard';
import { KsyosAccountCard } from '@frontend/components/pages/organizations/organizationAdd/KsyosAccountCard';
import { OverlayTemplate } from '@frontend/components/templates/OverlayTemplate';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, useAbortableFetch } from '@frontend/utils';
import { formatStringIntoDutchPostalCode } from '@frontend/utils/formatStringIntoDutchPostalCode';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { VektisOrganizationSettings } from '../utils/vektisUtils';
import {
    errorTypesByOrganizationProperty,
    updateValidationErrorsForDataErrors,
} from '../utils/organizationDataErrorsValidationUtils';
import {
    OrganizationRelationshipsActions,
    organizationRelationshipsReducer,
} from '../utils/organizationRelationshipsReducer';
import { AutoFillCard } from './autoFill/AutoFillCard';

const emptyOrganization: IOrganization = {
    areContactsAndTasksEnabled: false,
    country: 'NL',
    id: emptyUuid,
    isActive: true,
    isHighPrivacy: false,
    isMijnKsyosActive: false,
    kgoStatus: OrganizationKgoStatus.verified,
    name: '',
    organizationRelationships: [],
    organizationStructureType: OrganizationStructureType.organization,
};

export function OrganizationAdd() {
    assertHasAuthorization(ManagerAuthorization.organizationAddEdit);
    const navigate = useNavigate();

    const abortableFetch = useAbortableFetch();
    const theme = useTheme();

    const [relationshipState, dispatchRelationshipStateAction] = useReducer(
        organizationRelationshipsReducer<IOrganization>,
        {
            organization: emptyOrganization,
        }
    );
    const [isSaving, setIsSaving] = useState(false);
    const [organizationDataErrors, setOrganizationDataErrors] = useState<OrganizationDataError[]>([]);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [organizationId, setOrganizationId] = useState<string | undefined>();
    const translate = getSelectedLanguage();

    if (!relationshipState.organization) {
        return null;
    }

    if (organizationId) {
        return (
            <OverlayTemplate>
                <KgbSuccessPage
                    buttonText={translate.organization.viewOrganization}
                    linkTo={`/organizations/${organizationId}/view`}
                    text={formatString(translate.organization.canGetStarted, [
                        nonNullable(relationshipState.organization.name),
                    ])}
                    title={translate.organization.successful}
                />
            </OverlayTemplate>
        );
    }

    return (
        <OverlayTemplate
            leftButtons={
                <Button
                    isDisabled={isSaving}
                    onClick={() => navigate('/organizations')}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>
            }
            maxWidth={theme.pageLayout.width.medium}
            rightButtons={
                <Button
                    isDisabled={isSaving}
                    onClick={async () => await handleSubmit()}
                    variant="solid"
                >
                    {translate.organization.save}
                </Button>
            }
        >
            <Heading
                mb={10}
                size="xlarge"
            >
                {translate.organization.add}
            </Heading>
            <Row
                columnGap={6}
                wrap="wrap-reverse"
            >
                <Col span={[12, 12, 7]}>
                    <FormContextProvider
                        value={{
                            errors: errors,
                            item: relationshipState.organization,
                            onBlur: (key, value) => handleBlur(key, value),
                            onChange: (key, value) => handleChange(key, value),
                            onDoValidate: (key) => validateOne(key),
                            placeholderGroupName: 'organizationGeneralSettings',
                        }}
                    >
                        <GeneralInformationCard
                            dataErrors={organizationDataErrors}
                            dispatchRelationshipAction={dispatchRelationshipStateAction}
                            errors={errors}
                            handleChangeAndValidate={handleChangeAndValidate}
                            organization={relationshipState.organization}
                            setErrors={setErrors}
                        />

                        <ContactDetailsCard
                            errors={errors}
                            handleChangeAndValidate={handleChangeAndValidate}
                            organization={relationshipState.organization}
                        />

                        <KsyosAccountCard
                            errors={errors}
                            handleChange={handleChange}
                            organization={relationshipState.organization}
                        />
                    </FormContextProvider>
                </Col>
                <Col span={[12, 12, 5]}>
                    <AutoFillCard onAutoFill={handleAutoFill} />
                </Col>
            </Row>
        </OverlayTemplate>
    );

    function handleChange<K extends keyof IOrganization>(name: K, value: IOrganization[K]) {
        let newValue = value;

        if (relationshipState.organization?.country === 'NL' || relationshipState.organization?.country === null) {
            newValue =
                name === 'zip' && typeof value === 'string'
                    ? (formatStringIntoDutchPostalCode(value) as IOrganization[K])
                    : value;
        }

        dispatchRelationshipStateAction({
            payload: { key: name, value: newValue },
            type: OrganizationRelationshipsActions.updateOrganizationKeyValue,
        });
    }

    function handleBlur<K extends keyof IOrganization>(name: K, value: IOrganization[K]) {
        const trimmedValue = typeof value === 'string' ? value.trim() : value;

        dispatchRelationshipStateAction({
            payload: { key: name, value: trimmedValue },
            type: OrganizationRelationshipsActions.updateOrganizationKeyValue,
        });
    }

    function handleChangeAndValidate<K extends keyof IOrganization>(
        name: keyof IOrganization,
        value: IOrganization[K]
    ) {
        if (!relationshipState.organization) {
            return;
        }

        const organizationUpdate = { ...relationshipState.organization, [name]: value };

        dispatchRelationshipStateAction({
            payload: organizationUpdate,
            type: OrganizationRelationshipsActions.setOrganization,
        });
        setErrors(updateValidationErrorsForOne(organizationSchema, organizationUpdate, errors, name));
    }

    function handleAutoFill(autoFillOrganization: Partial<VektisOrganizationSettings>) {
        if (!relationshipState.organization) {
            return;
        }

        const organizationUpdate = { ...relationshipState.organization, ...autoFillOrganization };

        dispatchRelationshipStateAction({
            payload: organizationUpdate,
            type: OrganizationRelationshipsActions.setOrganization,
        });
    }

    async function validateOne(name: keyof IOrganizationValidated) {
        const { dataErrors, validationErrors } = await updateValidationErrorsForDataErrors(
            updateValidationErrorsForOne(organizationSchema, relationshipState.organization, errors, name),
            relationshipState.organization,
            name
        );

        setOrganizationDataErrors(dataErrors);
        setErrors(validationErrors);
    }

    async function handleSubmit() {
        setIsSaving(true);

        const { errors: schemaValidationErrors, item } = validate(organizationSchema, relationshipState.organization);

        const { dataErrors, validationErrors } = await updateValidationErrorsForDataErrors(
            schemaValidationErrors,
            relationshipState.organization,
            ...errorTypesByOrganizationProperty.keys()
        );

        setOrganizationDataErrors(dataErrors);

        if (validationErrors.size > 0) {
            setErrors(validationErrors);
        } else {
            setErrors(emptyValidationErrors);

            const url = '/api/organizations';
            const result = await abortableFetch.fetchJson(url, 'POST', item);

            if (result.status === 200) {
                setOrganizationId(result.json.id);
            }
        }

        setIsSaving(false);
    }
}
